import React, { useEffect, useRef } from 'react'
import SignInContainer from './components/sign-in-container'
import SignInFormContainer from './components/sign-in-form-container'
import SignInSubmitButton from './components/sign-in-submit-button'
import SignInFooter from './components/sign-in-footer'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import AuthCode, { AuthCodeRef } from 'react-auth-code-input'
import SignInBackButton from './components/sign-in-back-button'
import { signInInputSchema } from './data/sign-in-schema'
import {
  trialResendOtpTrialResendOtpPostMutation,
  trialVerifyOtpTrialVerifyOtpPostMutation,
} from '@/generated/@tanstack/react-query.gen'
import { toast } from 'react-toastify'
import { EUserCredentialStatus } from './sign-in'

interface VerifyCodeProps {
  email: string
  onVerifySuccess: () => void
  onClickBack: () => void
}

const formSchema = z.object({
  code: signInInputSchema.shape.code,
})

type FormValues = z.infer<typeof formSchema>

export default function SignInVerifyCode({
  email,
  onVerifySuccess,
  onClickBack,
}: VerifyCodeProps) {
  const {
    setValue,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
    },
  })
  const code = watch('code')
  const authInputRef = useRef<AuthCodeRef>(null)

  const handleOnChange = (res: string) => {
    setValue('code', res, { shouldValidate: res.length === 6 })
  }

  const { mutate: mutateVerifyCode, isPending: isPendingVerifyCode } =
    useMutation({
      ...trialVerifyOtpTrialVerifyOtpPostMutation({}),
      onSuccess: (response: any) => {
        const isSuccess = response.status === EUserCredentialStatus.AddPassword
        if (isSuccess) {
          toast.success(response.msg)
          onVerifySuccess()
          return
        }
        toast.error(response.msg)
      },
      onError: () => {
        setError('code', { message: 'Invalid verification code' })
      },
    })

  // Automatically submit if code is full copy/pasted in, or upon typing last digit of code
  useEffect(() => {
    if (code.length === 6) {
      mutateVerifyCode({
        body: { email, otp: code },
      })
    }
  }, [code])

  const { mutate: mutateResendEmail, isPending: isPendingResendEmail } =
    useMutation({
      ...trialResendOtpTrialResendOtpPostMutation({}),
      onSuccess: (response: any) => {
        if (response.status === EUserCredentialStatus.Success) {
          toast.success(response.msg)
          authInputRef.current?.clear()
        } else {
          toast.error(response.msg)
        }
      },
    })

  const isPending = isPendingVerifyCode || isPendingResendEmail

  const onResendCode = () => {
    mutateResendEmail({
      body: {
        email: email,
      },
    })
  }

  const onSubmit = (form: FormValues) => {
    mutateVerifyCode({
      body: {
        email: email,
        otp: form.code,
      },
    })
  }

  return (
    <SignInContainer loading={isPending}>
      <SignInFormContainer title="Check Your Email for the Code">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex space-y-4 flex-col py-4">
            <div className="text-center text-sm text-gray-600">
              <p>
                Email sent to{' '}
                <span className="font-semibold">{` ${email}`}</span>
              </p>
            </div>
            <div className="pb-2">
              <AuthCode
                ref={authInputRef}
                length={6}
                allowedCharacters="numeric"
                onChange={handleOnChange}
                containerClassName="flex justify-center gap-2"
                inputClassName="w-12 h-12 text-center text-xl border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00ac9c]"
              />
              {errors.code && (
                <p className="text-red-500 text-xs mt-2 text-center">
                  {errors.code.message}
                </p>
              )}
            </div>

            <SignInSubmitButton text="Submit Code" />

            <div
              onClick={onResendCode}
              className="text-center text-primary hover:text-blue-800 hover:font-medium cursor-pointer transition duration-200 ease-in-out px-7 py-2 text-sm uppercase"
            >
              Resend code
            </div>

            <SignInBackButton onClick={onClickBack} />
          </div>
        </form>
      </SignInFormContainer>
      <SignInFooter />
    </SignInContainer>
  )
}
